import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';

const samplePosts = [
  {
    author: "Marcel van Oost",
    role: "Connecting the dots in FinTech...",
    date: "1d • Edited",
    content: "🚨 Mastercard x PayPal join forces to bring Agentic AI Payments mainstream...",
    image: "/assets/post-display-photo-1.jpeg",
    reactions: 967,
    avatar: "/assets/profile-display-photo-4.jpeg"
  },
  {
    author: "Cristina Junqueira",
    role: "Co-Founder at Nubank",
    date: "1w",
    content: "I had the honor of joining a fireside chat at the Americas Society/Council of the Americas event with Luis Alberto Moreno about 'Shaping the Future of Finance through Innovation'",
    image: "/assets/post-display-photo-2.jpeg",
    reactions: 713,
    avatar: "/assets/profile-display-photo-5.jpeg"
  },
  {
    author: "Rebekah Rice",
    role: "I Help Driven Professionals Turn Their Value Into Better Roles & Higher Pay",
    date: "9mo • Edited",
    content: "Just crossed 1M transactions processed! Grateful for our amazing team and the trust our users place in us every day. 🚀",
    image: "/assets/post-display-photo-3.jpeg",
    reactions: 655,
    avatar: "/assets/profile-display-photo-6.jpeg"
  }
];

const LinkedInPost = ({ post, zIndex, opacity }) => {
  return (
    <div 
      className="absolute top-0 left-0 right-0 transition-all duration-500 ease-out"
      style={{
        zIndex,
        opacity,
        transform: `translateY(${(2 - zIndex) * 10}px) scale(${1 - (2 - zIndex) * 0.03})`
      }}
    >
      <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden max-w-2xl mx-auto border border-gray-100">
        {/* Header */}
        <div className="p-3 md:p-6 flex items-start gap-2 md:gap-4">
          <img 
            src={post.avatar} 
            alt={post.author}
            className="w-8 h-8 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-xs md:text-lg truncate">{post.author}</h3>
            <p className="text-gray-600 text-[10px] md:text-sm truncate">{post.role}</p>
            <p className="text-gray-500 text-[9px] md:text-xs mt-0.5 md:mt-1">{post.date}</p>
          </div>
          <button className="text-gray-400 hover:bg-gray-50 p-1 md:p-2 rounded-lg transition-colors flex-shrink-0">
            <svg className="w-3 h-3 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-3 md:px-6 pb-2 md:pb-4">
          <p className="text-gray-800 leading-relaxed text-[10px] md:text-base line-clamp-2 md:line-clamp-none">{post.content}</p>
        </div>

        {/* Image */}
        {post.image && (
          <div className="relative w-full aspect-video bg-gray-100">
            <img 
              src={post.image} 
              alt="Post content"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Footer */}
        <div className="px-3 md:px-6 py-2 md:py-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-[10px] md:text-sm text-gray-600">
            <span className="flex items-center gap-1 md:gap-2">
              <span className="text-gray-700 font-medium">{post.reactions.toLocaleString()} reactions</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LynkrDemoSection = () => {
  const [animationState, setAnimationState] = useState('idle');

  useEffect(() => {
    let timeouts = [];

    const runAnimation = () => {
      // Show user message
      timeouts.push(setTimeout(() => setAnimationState('user-message'), 500));
      
      // Show loading
      timeouts.push(setTimeout(() => setAnimationState('loading'), 2000));
      
      // Show posts
      timeouts.push(setTimeout(() => {
        setAnimationState('posts');
      }, 3500));
      
      // Reset and loop
      timeouts.push(setTimeout(() => {
        setAnimationState('idle');
      }, 9000));
      
      // Start over
      timeouts.push(setTimeout(runAnimation, 10000));
    };

    runAnimation();

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <section className="py-4 md:py-8 px-2 md:px-6">
      <div className="max-w-7xl mx-auto">

      <div className="relative min-h-[400px] md:min-h-[700px]">
          {/* User Message - Top Right */}
          <div className="absolute top-0 right-0 max-w-[200px] md:max-w-md z-50"> {/* <- ensure bubble is on top */}
            <div 
              className={`transition-all duration-700 ${
                animationState !== 'idle' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white text-black rounded-2xl md:rounded-3xl rounded-tr-sm px-3 py-2 md:px-6 md:py-4 shadow-lg pointer-events-auto">
                <p className="text-xs md:text-lg">What are some recent viral posts in fintech?</p>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 justify-end mr-2 md:mr-4">
                <span className="text-xs md:text-sm text-gray-500">You</span>
                <MessageCircle className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Response - Below User Message on Left */}
          {/* NOTE: bumped top down (mobile top-20, md top-28) and set z-index so bubble remains above */}
          <div className="absolute top-24 md:top-28 left-0 w-full max-w-3xl z-10">
            {/* Loading State - now absolutely positioned so it shares the same top as posts */}
            <div
              className={`absolute w-2/3 md:w-1/2 inset-x-0 top-0 z-20 transition-all duration-700 ${
                animationState === 'loading' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="bg-white rounded-2xl md:rounded-3xl rounded-tl-sm px-3 py-2 md:px-6 md:py-4 shadow-lg">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex gap-0.5 md:gap-1">
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-xs md:text-base text-gray-600">Finding viral posts...</span>
                </div>
              </div>
              <div className="flex items-center gap-1 md:gap-2 mt-1 md:mt-2 ml-2 md:ml-4">
                <span className="text-xs md:text-sm text-gray-500">Lynkr</span>
              </div>
            </div>

            {/* Posts State - also absolutely positioned at the same top so it starts exactly where loading started */}
            <div
              className={`w-5/6 md:w-4/5 absolute inset-x-0 top-0 z-10 transition-all duration-700 ${
                animationState === 'posts' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <div className="relative h-[320px] md:h-[480px]">
                <LinkedInPost post={samplePosts[0]} zIndex={3} opacity={1} />
                <LinkedInPost post={samplePosts[1]} zIndex={2} opacity={0.6} />
                <LinkedInPost post={samplePosts[2]} zIndex={1} opacity={0.3} />
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default LynkrDemoSection;
